// Configurable API base so the web app can target the Node API (same-origin
// "/api" via the Vite proxy) or the Cloudflare Worker directly during the
// migration. Set VITE_API_BASE in apps/web/.env, e.g.
//   VITE_API_BASE=http://localhost:8787/api   (local Worker)
//   VITE_API_BASE=https://aria-api.<acct>.workers.dev/api
// Trailing slashes are trimmed. Defaults to "/api".
export const API_BASE = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public status: number, message: string, public data?: unknown) {
    super(message);
    this.name = "ApiError";
  }
}

// A 401 from the login/register/MFA-verify endpoints is a *normal*
// invalid-credentials (or invalid TOTP code) response and must surface to the
// form — it is not a session expiry, so we don't clear state or redirect for it.
function isAuthAttempt(path: string): boolean {
  return /\/auth\/(login|register|mfa\/verify)\b/.test(path);
}

// Session expired (or token revoked): drop the stored token, purge the offline
// API cache so the next person can't read it, and bounce to the login screen.
// Guarded so we only ever redirect once and never loop on /login itself.
let redirecting = false;
function handleSessionExpiry(): void {
  if (redirecting) return;
  redirecting = true;
  try {
    localStorage.removeItem("aria.token");
  } catch {
    /* ignore storage failures */
  }
  if (typeof caches !== "undefined") {
    void caches.delete("aria-api-cache");
  }
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

export const apiClient = {
  async get<T>(path: string, init?: RequestInit): Promise<T> {
    return request<T>("GET", path, undefined, init);
  },
  async post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>("POST", path, body, init);
  },
  async put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>("PUT", path, body, init);
  },
  async patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return request<T>("PATCH", path, body, init);
  },
  async del<T>(path: string, init?: RequestInit): Promise<T> {
    return request<T>("DELETE", path, undefined, init);
  },
  /**
   * Raw-body file upload with progress. XHR because fetch has no upload
   * progress events; mirrors request()'s auth + error semantics.
   */
  uploadFile<T>(path: string, file: File | Blob, onProgress?: (pct: number) => void): Promise<T> {
    const token = localStorage.getItem("aria.token");
    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_BASE}${path}`);
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        let data: unknown;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = undefined;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data as T);
          return;
        }
        if (xhr.status === 401 && !isAuthAttempt(path)) handleSessionExpiry();
        let message = "Upload failed";
        if (data && typeof data === "object" && "message" in data) {
          const msg = (data as Record<string, unknown>).message;
          if (typeof msg === "string") message = msg;
        }
        reject(new ApiError(xhr.status, message, data));
      };
      xhr.onerror = () => reject(new ApiError(0, "Network error during upload"));
      xhr.send(file);
    });
  },
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  init?: RequestInit
): Promise<T> {
  const token = localStorage.getItem("aria.token");
  const headers = new Headers(init?.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    method,
    headers,
    body: body ? JSON.stringify(body) : init?.body,
  });

  if (!res.ok) {
    // Global session-expiry handling: any authenticated 401 (i.e. not a
    // login/register attempt) means the token is gone/stale — clear it and
    // redirect. ApiError is still thrown so in-flight callers unwind normally.
    if (res.status === 401 && !isAuthAttempt(path)) {
      handleSessionExpiry();
    }
    let data: unknown;
    try {
      data = await res.json();
    } catch {
      data = undefined;
    }
    let message = res.statusText;
    if (data && typeof data === "object" && "message" in data) {
      const msg = (data as Record<string, unknown>).message;
      if (typeof msg === "string") message = msg;
    }
    if (!message) message = "Request failed";
    throw new ApiError(res.status, message, data);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}